import React from 'react';
import {
  QueryClient,
  QueryClientProvider as ReactQueryClientProvider,
  QueryCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from 'react-toastify';
import { Logger } from '../utils/logger';

// Optimized caching configuration to reduce Supabase egress
// Different data types have different staleness characteristics:
// - User/Account data: Changes infrequently (cache 5 min)
// - Orders/Tasks: May change frequently (cache 1 min) 
// - Settings/Config: Rarely changes (cache 10 min)

// Create a client with optimized caching
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 10 minutes by default (significantly reduces redundant fetches)
      staleTime: 10 * 60 * 1000, // 10 minutes - increased from 1 minute to reduce egress
      
      // Retry failed requests once
      retry: 1,
      
      // Don't refetch on window focus (was causing excessive data usage)
      refetchOnWindowFocus: false,
      
      // Keep data in cache for 30 minutes after last use
      gcTime: 30 * 60 * 1000, // 30 minutes - increased from 10 minutes
      
      // Don't refetch when reconnecting to network (manual refresh only)
      refetchOnReconnect: false,
      
      // Always attempt queries regardless of network state
      networkMode: 'always',
      
      // Don't refetch in background while data is still fresh
      refetchInterval: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      
      // Keep mutation data for 5 minutes
      gcTime: 5 * 60 * 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Get additional context from meta if available
      const meta = query.meta || {};
      const domain = (meta.domain as string) || 'unknown';
      const entityType = (meta.entityType as string) || 'data';
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Only show error toasts if we have data in the cache
      // or this is not a first-time load error (which will be handled by the component)
      if (query.state.data !== undefined) {
        // This is a background update that failed
        toast.error(`Failed to update ${entityType} in the background: ${errorMessage}`);
      } else {
        // This is a fetch with no previous data
        // Could be initial load or a refresh after cache expiration
        // Let the component handle this type of error
        Logger.error(`Query error in ${domain} ${entityType}`, {
          context: 'QueryCache',
          operation: 'onError',
          domain,
          entityType,
          errorMessage,
          hasData: false,
        });
      }
    },
  }),
});

interface QueryClientProviderProps {
  children: React.ReactNode;
}

export function QueryClientProvider({ children }: QueryClientProviderProps) {
  // DISABLED: Tab visibility handling to reduce Supabase egress
  // Manual refresh only - no automatic refetching on tab focus
  // React.useEffect(() => {
  //   let wasHidden = document.hidden;

  //   const handleVisibilityChange = () => {
  //     const isHidden = document.hidden;

  //     if (!isHidden && wasHidden) {
  //       // Tab became visible after being hidden
  //       // Removed verbose logging - was causing too much noise
  //       // Logger.info('Tab became visible, resuming queries')

  //       // Resume any paused mutations
  //       queryClient.resumePausedMutations();

  //       // Only refetch queries that are actually stale (older than 5 minutes)
  //       // This prevents unnecessary refetching when switching tabs frequently
  //       queryClient.refetchQueries({
  //         stale: true,
  //         type: 'active',
  //       });
  //     }

  //     wasHidden = isHidden;
  //   };

  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //   return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  // }, []);

  return (
    <ReactQueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development mode */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </ReactQueryClientProvider>
  );
}

export default QueryClientProvider;
